import { Link } from "@tiptap/extension-link";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useState, type ReactNode } from "react";
import { IconCode, IconLink, IconList } from "../../../components/icons";
import { normalizeEditorHtml } from "../../../lib/html/normalizeEditorHtml";

type AdminFieldSize = "default" | "compact";

type MinimalHtmlEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  size?: AdminFieldSize;
};

type EditorMode = "visual" | "code";

const inputSizeClass: Record<AdminFieldSize, string> = {
  default: "rounded-xl px-3.5 py-2.5 text-[15px]",
  compact: "rounded-lg px-2.5 py-1.5 text-sm",
};

const editorMinHeight: Record<AdminFieldSize, string> = {
  default: "min-h-[88px]",
  compact: "min-h-[96px]",
};

function ToolbarButton({
  title,
  active,
  disabled,
  onClick,
  children,
}: {
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={[
        "inline-flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 font-onest text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        active
          ? "bg-brand-100 text-brand-700"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-800",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function VisualToolbar({
  editor,
  onInsertLink,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  onInsertLink: () => void;
}) {
  const toolbar = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      isBold: current.isActive("bold"),
      isItalic: current.isActive("italic"),
      isList: current.isActive("bulletList"),
    }),
  });

  return (
    <>
      <ToolbarButton
        title="Жирный"
        active={toolbar.isBold}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <span className="font-bold">B</span>
      </ToolbarButton>
      <ToolbarButton
        title="Курсив"
        active={toolbar.isItalic}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <span className="italic">I</span>
      </ToolbarButton>
      <ToolbarButton title="Ссылка" onClick={onInsertLink}>
        <IconLink size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Маркированный список"
        active={toolbar.isList}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <IconList size={14} />
      </ToolbarButton>
    </>
  );
}

export function MinimalHtmlEditor({
  value,
  onChange,
  placeholder,
  rows = 4,
  size = "default",
}: MinimalHtmlEditorProps) {
  const [mode, setMode] = useState<EditorMode>("visual");
  const [codeDraft, setCodeDraft] = useState(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        code: false,
        strike: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
    ],
    content: value || "",
    immediatelyRender: false,
    onUpdate: ({ editor: current }) => {
      onChange(normalizeEditorHtml(current.getHTML()));
    },
    editorProps: {
      attributes: {
        class: [
          "minimal-html-editor__surface tiptap",
          inputSizeClass[size],
          editorMinHeight[size],
          "max-h-64 overflow-y-auto outline-none",
        ].join(" "),
        ...(placeholder ? { "data-placeholder": placeholder } : {}),
      },
    },
  });

  useEffect(() => {
    setCodeDraft(value);
  }, [value]);

  useEffect(() => {
    if (!editor || mode !== "visual") return;

    const current = normalizeEditorHtml(editor.getHTML());
    const next = normalizeEditorHtml(value);
    if (current !== next) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [editor, mode, value]);

  const insertLink = () => {
    if (!editor) return;

    const previousUrl = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL ссылки", previousUrl || "https://");
    if (url === null) return;

    if (url.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url.trim() })
      .run();
  };

  const switchToCode = () => {
    const html = editor
      ? normalizeEditorHtml(editor.getHTML())
      : normalizeEditorHtml(value);
    setCodeDraft(html);
    onChange(html);
    setMode("code");
  };

  const switchToVisual = () => {
    const html = normalizeEditorHtml(codeDraft);
    onChange(html);
    editor?.commands.setContent(html || "", { emitUpdate: false });
    setMode("visual");
  };

  const switchMode = (next: EditorMode) => {
    if (next === mode) return;
    if (next === "code") switchToCode();
    else switchToVisual();
  };

  return (
    <div className="minimal-html-editor overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-500/20">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 bg-slate-50/80 px-1.5 py-1">
        {mode === "visual" && editor ? (
          <VisualToolbar editor={editor} onInsertLink={insertLink} />
        ) : (
          <span className="px-1.5 font-onest text-[11px] text-slate-500">
            Редактирование HTML
          </span>
        )}

        <span className="ml-auto flex items-center gap-0.5">
          <ToolbarButton
            title="Визуальный режим"
            active={mode === "visual"}
            disabled={!editor && mode !== "visual"}
            onClick={() => switchMode("visual")}
          >
            <span className="font-medium">Aa</span>
          </ToolbarButton>
          <ToolbarButton
            title="HTML-код"
            active={mode === "code"}
            onClick={() => switchMode("code")}
          >
            <IconCode size={14} />
          </ToolbarButton>
        </span>
      </div>

      {mode === "visual" ? (
        <EditorContent editor={editor} />
      ) : (
        <textarea
          value={codeDraft}
          rows={rows}
          placeholder={placeholder}
          onChange={(event) => {
            const next = event.target.value;
            setCodeDraft(next);
            onChange(next);
          }}
          className={[
            "w-full resize-y border-0 bg-transparent font-mono text-slate-800 outline-none placeholder:text-slate-400",
            inputSizeClass[size],
            editorMinHeight[size],
          ].join(" ")}
        />
      )}
    </div>
  );
}
